import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { CommonModule, formatDate } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatExpansionModule } from '@angular/material/expansion';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatIcon } from '@angular/material/icon';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { DepartmentQuestion, OffboardingDepartment, OffboardingSection, OffboardingSectionKey, OffboardingStatusOption, viewOffboardingAttachements, viewOffboardingDetails } from './maintenance-offboarding-interface/maintenance-offboarding.interface';
import { MatDialog } from '@angular/material/dialog';
import { NewRepApplicationService } from '../../../agent-onboarding/new-rep-application/new-rep-application-services/new-rep-application.service';
import { MaintenanceOffboardingService } from './maintenance-offboarding-service/maintenance-offboarding.service';
import { HttpStatusCode } from 'axios';
import { ConfirmPopupComponent } from '../../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { Router } from '@angular/router';
import { MaintenanceOffboardingModel } from './maintenance-offboarding-models/maintenance-offboarding.models';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';

/** DD/MM/YYYY datepicker convention shared with the onboarding forms. */
export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-maintenance-offboarding',
  providers: [provideMomentDateAdapter(MY_FORMATS)],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatRadioModule,
    MatExpansionModule,
    MatIcon
  ],
  templateUrl: './maintenance-offboarding.component.html',
  styleUrl: './maintenance-offboarding.component.scss'
})
export class MaintenanceOffboardingComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() readonly = signal(false);
  @Output() save = new EventEmitter<string>();
  @Output() preview = new EventEmitter<string[]>();
  private fb = inject(FormBuilder);
  private snackBarService = inject(SnackbarService);
  private uploadAttachemntService = inject(NewRepApplicationService);
  private sessionStorageService = inject(SessionStorageService);
  private matDialog = inject(MatDialog);
  private offboardingMaintananceService = inject(MaintenanceOffboardingService);
  private router = inject(Router);
  setSprinner = signal(false);
  data = history.state;
  viewOffboardingAttachements!: viewOffboardingAttachements[];
  viewOffboardingDetails!: viewOffboardingDetails;
  /**
   * The rep's status through the off-boarding cycle: off-boarding is triggered
   * while a rep is IN FORCE, then they move to SERVING NOTICE and finally
   * RESIGNED. Off-boarding itself is a stage flag, never a status, so it is not
   * an option here.
   */
  statuses: OffboardingStatusOption[] = [];

  /**
   * The form's sections, in render order. Each becomes one expansion panel with
   * its own Save button; the `key` also drives which body template renders.
   */
  readonly sections: OffboardingSection[] = [
    {
      key: 'resignation',
      title: 'Resignation Details',
      icon: 'assignment_late',
      description: 'Status, resignation dates, DSU charges and the manager\'s email.'
    },
    {
      key: 'clearance',
      title: 'Department Clearance Questionnaire',
      icon: 'fact_check',
      description: 'Department clearance answers, their attachments and the exit form.'
    },
    {
      key: 'remarks',
      title: 'Exit Form & Remarks',
      icon: 'notes',
      description: 'Any additional notes about this off-boarding.'
    }
  ];

  /** Index of the open panel — one at a time, Resignation Details first. */
  activePanelIndex = -1;

  /** Sections saved in this sitting, keyed by section — drives the header badge. */
  private readonly savedSections = signal<Partial<Record<OffboardingSectionKey, boolean>>>({});

  /**
   * Department clearance questionnaire. Each block renders its questions in order
   * and carries exactly one attachment control.
   */
  readonly departments: OffboardingDepartment[] = [
    {
      title: 'Finance Department',
      icon: 'account_balance',
      questions: [
        {
          control: 'financeOutstandingPayments',
          text: 'Are there any outstanding payments to PIAS? If yes, what is the amount?',
          amountControl: 'financeOutstandingPaymentsAmount',
          remarksControl: 'financeOutstandingPaymentscomments'
        }
      ],
      // attachmentControl: 'Finance Department Attachment',
      // attachmentLabel: 'Finance Department Attachment'
    },
    {
      title: 'Commissions Department',
      icon: 'payments',
      questions: [
        {
          control: 'commissionsNegativeBalance',
          text: 'Is the leaving Rep in negative balance? If yes, what is the amount?',
          amountControl: 'commissionsNegativeBalanceAmount',
          remarksControl: 'commissionsNegativeBalancecomments',
        },
        {
          control: 'commissionsIndemnityExposure',
          text: 'Does the leaving Rep has any indemnity exposure? If yes, what is the amount and please advise further actions.',
          amountControl: 'commissionsIndemnityExposureAmount',
          actionControl: 'commissionsIndemnityExposureActions',
          remarksControl: 'commissionsIndemnityExposurecomments',
          actionLabel: 'Further Actions',
        }
      ],
      // attachmentControl: 'Commissions Department Attachment',
      // attachmentLabel: 'Commissions Department Attachment'
    },
    {
      title: 'Business Support Department',
      icon: 'support_agent',
      questions: [
        {
          control: 'businessSupportOutstandingPremiums',
          text: 'Are there any outstanding premiums for GI policies? If yes, what is the amount?',
          amountControl: 'businessSupportOutstandingPremiumsAmount',
          remarksControl: 'businessSupportOutstandingPremiumscomments',
        }
      ],
      // attachmentControl: 'Business Support Department Attachment',
      // attachmentLabel: 'Business Support Department Attachment'
    }
  ];

  readonly message = 'This field is required.';
  currentUserRole!: string;

  async onlastDateSave(index: number): Promise<boolean> {
    if (this.sections[index]?.key === 'resignation') {
      const lastDayOfService =
        this.formGroup.get('lastDayOfService')?.value;

      if (lastDayOfService) {
        const selected = typeof lastDayOfService.toDate === 'function'
          ? lastDayOfService.toDate()
          : new Date(lastDayOfService);

        const today = new Date();

        selected.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        if (selected.getTime() === today.getTime()) {
          const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
            width: '500px',
            disableClose: true,
            data: {
              message: 'The Last Day of Service is today. Do you want to continue?',
              hidecancel: false,
            }
          });

          const result = await dialogRef.afterClosed().toPromise();
          return result?.data === true;
        }
      }
    }

    return true;
  }



  private saveAndAdvanceSection(index: number): void {
    const saved = this.saveSection(index, true,false);

    if (saved && index + 1 < this.sections.length) {
      this.activePanelIndex = index + 1;
    }
  }


  async ngOnInit(): Promise<void> {
    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();
    const add = (name: string, value: unknown = '',
      validators: ValidatorFn | ValidatorFn[] = []) => {
      if (!this.formGroup.get(name)) {
        this.formGroup.addControl(name, this.fb.control(value, validators));
      }
    };

    // Off-boarding details
    add('status');
    // Dates start as null so the pickers open empty rather than on an invalid value.
    add('resignedDatePias', null);
    add('lastDayOfService', null);
    add('reasonForLeaving');
    add('dsuCharges');

    // Attachment — email PDF from the manager
    add('managerEmailAttachment', null, [Validators.required]);

    // Department clearance questionnaire — answer, amount, optional follow-up
    // text per question, plus one attachment per department.
    for (const department of this.departments) {
      for (const question of department.questions) {
        add(question.control);
        add(question.amountControl);
        add(question.remarksControl);
        if (question.actionControl) {
          add(question.actionControl);
        }
      }
    }

    add('Department Attachment', null, [Validators.required]);
    // Exit form — collected alongside the clearance questionnaire.
    add('exitFormAttachment', null, [Validators.required]);
    add('finalexitFormAttachment', null, [Validators.required]);

    add('remarks');

    this.formGroup.controls['status'].setValue(this.data.applicant.repstatus);
    if (this.data.applicant.repstatus == 'Resigned') {
      this.statuses = [{ label: 'INFORCE', value: 'Inforced' },
      { label: 'SERVING NOTICE', value: 'Serving Notice' },
      { label: 'RESIGNED', value: 'Resigned' }
      ];

    } else {
      this.statuses = [{ label: 'INFORCE', value: 'Inforced' },
      { label: 'SERVING NOTICE', value: 'Serving Notice' },
      ];
    }

    await this.getOffboardingDetails();

  }



  // ── Section panels ─────────────────────────────────────────
  isPanelExpanded(index: number): boolean {
    return this.activePanelIndex === index;
  }

  onPanelOpened(index: number): void {
    this.activePanelIndex = index;
  }

  onPanelClosed(index: number): void {
    if (this.activePanelIndex === index) {
      this.activePanelIndex = -1;
    }
  }

  isSaved(key: OffboardingSectionKey): boolean {
    return !!this.savedSections()[key];
  }

  /** The final panel has nothing to continue to, so it just saves. */
  isLastSection(index: number): boolean {
    return index === this.sections.length - 1;
  }

  /**
   * Saves one section: only that section's controls are validated, so an
   * incomplete section elsewhere on the form can't block it.
   */
  // onSectionSave(index: number): void {
  //   this.saveSection(index);
  // }
  async onSectionSave(index: number): Promise<void> {
    const confirmed = await this.onlastDateSave(index);

    if (!confirmed) {
      return;
    }

    this.saveAndAdvanceSection(index);
  }

  /** Saves this section and, once it is valid, opens the next panel. */
  async triggeremail(index: number): Promise<void> {

    const section = this.sections[index];
    if (section.key === 'remarks') {
      const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
        width: '500px',
        disableClose: true,
        data: {
          message: 'Are you sure you want to submit?',
          hidecancel: false
        }
      });

      const result = await dialogRef.afterClosed().toPromise();

      if (result?.data !== true) {
        return;
      }
    }
    const controls = this.controlsFor(section.key)
      .map((name) => this.formGroup.get(name))
      .filter((control): control is NonNullable<typeof control> => !!control);

    if (controls.every((control) => control.valid)) {
      const confirmed = await this.onlastDateSave(index);

      if (!confirmed) {
        return;
      }
      this.setSprinner.set(true);
      this.saveSection(index, false,true);
      let payload;
      if (index === 0) {
        payload = {
          "pias_rep_code": this.data.applicant.repcode,
          "file_path": "/sample_documents/Exit Form for Resigned Representative_vMay2026.pdf",
          "file_name": "Exit Form for Resigned Representative_vMay2026.pdf",
          "template": "A10"
        }

      } else if (index === 1) {
        let filePath;
        let fileName;
        this.viewOffboardingAttachements?.forEach(item => {
          if (item?.onboardingDocumentType === 'exitFormAttachment') {
            filePath = item.filePath,
              fileName = item.fileName
          }
        });
        payload = {
          "pias_rep_code": this.data.applicant.repcode,
          "file_path": filePath,
          "file_name": fileName,
          "template": "A11"
        }

      } else if (index === 2) {
        let filePath;
        let fileName;
        this.viewOffboardingAttachements?.forEach(item => {
          if (item?.onboardingDocumentType === 'finalexitFormAttachment') {
            filePath = item.filePath,
              fileName = item.fileName
          }
        });

        payload = {
          "pias_rep_code": this.data.applicant.repcode,
          "file_path": filePath,
          "file_name": fileName,
          "template": "A12"
        };
      }
      const responseData = await this.offboardingMaintananceService
        .triggerEmail(payload)
        .then((response) => {
          return JSON.parse(response);
        })
        .catch((error) => {
          // console.error(error);
          return error;
        });
      this.setSprinner.set(false);

      if (responseData.status === HttpStatusCode.Ok) {
        if (section.key === 'resignation' || section.key === 'clearance') {
          this.snackBarService.openSuccessSnackBar(`${section.title} email triggered successfully!`);
        }
        const next = index + 1;
        if (next < this.sections.length) {
          this.activePanelIndex = next;
        } else {
          this.activePanelIndex = -1;
          this.save.emit('saved');
        }

      } else {
        if (responseData.code === HttpStatusCode.BadRequest) {
          // Display notification on API failure.
          this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
        }
        if (responseData.code === HttpStatusCode.Unauthorized) {
          this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
          this.router.navigate(['/login']);
        }
      }

    } else {
      controls.forEach((control) => control.markAsTouched());
      this.snackBarService.openWarningSnackBar('Please resolve the highlighted fields before saving.');
    }
  }

  /**
   * The shared save: validates the section's own controls, and reports whether it
   * went through so `saveAndContinue` knows not to advance past a failed section.
   */
  private saveSection(index: number, saved: boolean, submitted: boolean): boolean {
    const section = this.sections[index];
    if (!section) {
      return false;
    }

    const controls = this.controlsFor(section.key)
      .map((name) => this.formGroup.get(name))
      .filter((control): control is NonNullable<typeof control> => !!control);

    if (controls.some((control) => control.invalid)) {
      controls.forEach((control) => control.markAsTouched());
      this.snackBarService.openWarningSnackBar('Please resolve the highlighted fields before saving.');
      return false;
    }
    let saveOffboardingDetails = new MaintenanceOffboardingModel();
    saveOffboardingDetails.setPiasRepCode(this.data.applicant.repcode);
    saveOffboardingDetails.setoffboardingId(this.viewOffboardingDetails == null ? 0 : this.viewOffboardingDetails.id);
    if (index == 0) {
      saveOffboardingDetails.setrepStatus(this.formGroup.get('status')?.value);
      saveOffboardingDetails.setdateOfResigned(this.formGroup.get('resignedDatePias')?.value ? formatDate(this.formGroup.get('resignedDatePias')?.value, 'yyyy-MM-dd', 'en-US') : '');
      saveOffboardingDetails.setlastDayOfService(this.formGroup.get('lastDayOfService')?.value ? formatDate(this.formGroup.get('lastDayOfService')?.value, 'yyyy-MM-dd', 'en-US') : '');
      saveOffboardingDetails.setdsuCharges(this.formGroup.get('dsuCharges')?.value);
      saveOffboardingDetails.setreasonForLeaving(this.formGroup.get('reasonForLeaving')?.value);
    } else if (index == 1) {
      saveOffboardingDetails.setQuestionFinOutstandingPayment(this.formGroup.get('financeOutstandingPayments')?.value);
      saveOffboardingDetails.setAmountFinOutstandingPayment(this.formGroup.get('financeOutstandingPaymentsAmount')?.value);
      saveOffboardingDetails.setQuestionComNegativeBalance(this.formGroup.get('commissionsNegativeBalance')?.value);
      saveOffboardingDetails.setAmountComNegativeBalance(this.formGroup.get('commissionsNegativeBalanceAmount')?.value);
      saveOffboardingDetails.setQuestionComIndemnityExposure(this.formGroup.get('commissionsIndemnityExposure')?.value);
      saveOffboardingDetails.setAmountComIndemnityExposure(this.formGroup.get('commissionsIndemnityExposureAmount')?.value);
      saveOffboardingDetails.setActionComIndemnityExposure(this.formGroup.get('commissionsIndemnityExposureActions')?.value);
      saveOffboardingDetails.setQuestionBusGiPolicies(this.formGroup.get('businessSupportOutstandingPremiums')?.value);
      saveOffboardingDetails.setAmountBusGiPolicies(this.formGroup.get('businessSupportOutstandingPremiumsAmount')?.value);

      saveOffboardingDetails.setClearanceQuestionnaireRemarks1(this.formGroup.get('financeOutstandingPaymentscomments')?.value);
      saveOffboardingDetails.setClearanceQuestionnaireRemarks2(this.formGroup.get('commissionsNegativeBalancecomments')?.value);
      saveOffboardingDetails.setClearanceQuestionnaireRemarks3(this.formGroup.get('commissionsIndemnityExposurecomments')?.value);
      saveOffboardingDetails.setClearanceQuestionnaireRemarks4(this.formGroup.get('businessSupportOutstandingPremiumscomments')?.value);

    } else if (index == 2) {
      saveOffboardingDetails.setrepStatus(this.formGroup.get('status')?.value);
      saveOffboardingDetails.setoffboardingRemarks(this.formGroup.get('remarks')?.value);
      if (submitted == true) {
        saveOffboardingDetails.setdeclarationName(this.sessionStorageService.getCurrentUserRole());
        saveOffboardingDetails.setdeclarationDate(new Date().toISOString());
      }

    }

    this.saveOffboardingDetails(saveOffboardingDetails);

    this.savedSections.update((saved) => ({ ...saved, [section.key]: true }));
    if (saved == true) {
      this.snackBarService.openSuccessSnackBar(`${section.title} saved successfully!`);
    }
    return true;
  }

  /** The controls a section owns — the scope of its Save. */
  private controlsFor(key: OffboardingSectionKey): string[] {
    if (key === 'resignation') {
      return [
        'status',
        'resignedDatePias',
        'lastDayOfService',
        'reasonForLeaving',
        'dsuCharges',
        'managerEmailAttachment',
      ];
    }

    if (key === 'clearance') {
      const names: string[] = [];
      for (const department of this.departments) {
        for (const question of department.questions) {
          names.push(question.control, question.amountControl);
          if (question.actionControl) {
            names.push(question.actionControl);
          }
        }
      }
      names.push('Department Attachment');
      names.push('exitFormAttachment');
      return names;
    }


    return ['remarks'];
  }


  /**
   * Answering "No" drops the amount and follow-up text — otherwise a value the
   * user can no longer see would still be submitted.
   */

  onAnswerChange(question: DepartmentQuestion): void {
    const answer = this.formGroup.get(question.control);
    const relatedControls = [
      question.amountControl,
      question.actionControl,
      question.remarksControl
    ].filter((name): name is string => !!name);

    if (answer?.value === true) {
      this.formGroup
        .get(question.amountControl)
        ?.setValidators([Validators.required]);
    } else {
      relatedControls.forEach(name => {
        const control = this.formGroup.get(name);
        control?.reset();
        control?.clearValidators();
      });
    }

    relatedControls.forEach(name => {
      this.formGroup.get(name)?.updateValueAndValidity();
    });
  }

  onAttachmentFileChange(event: any, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const control = this.formGroup.get(controlName);

    // ✅ Valid file → update form properly
    this.formGroup.patchValue({
      [controlName]: file?.name
    });

    if (!control) return;

    // Reset first
    control.markAsTouched();
    control.setErrors(null);

    if (!file) {
      control.setValue(null);
      return;
    }

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/png'
    ]);

    const maxSize = 5 * 1024 * 1024; // 5MB

    // Validate type
    if (!allowedTypes.has(file.type)) {
      control.setErrors({ invalidType: true });
      return;
    }

    // Validate size
    if (file.size > maxSize) {
      control.setErrors({ maxSize: true });
      return;
    }

    control.updateValueAndValidity();
    this.uploaddocument(file, controlName);
  }

  async uploaddocument(file: File, controlName: string) {

    const formData = new FormData();

    formData.append('file', file);
    formData.append('Description', controlName);
    formData.append('filename', file?.name);
    formData.append('ResponseValue', '0');
    formData.append('UserId', String(this.data.applicant.onboardingid));

    const responseData = await this.uploadAttachemntService
      .uploadDocuments(formData)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    if (responseData.status === HttpStatusCode.Ok) {
      this.formGroup.patchValue({
        [controlName]: responseData.data.responsedata
      });
      this.viewOffboardingDocumants();
      this.snackBarService.openSuccessSnackBar("document uploaded successfully!");
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
    }
  }

  async viewOffboardingDocumants() {
    let repCode = this.data.applicant.repcode;
    const responseData = await this.offboardingMaintananceService
      .getOffboardingDetails(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });

    this.viewOffboardingAttachements = responseData.data.responsedata.documents;
  }

  removeFile(controlName: string) {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: 'Do you want to remove the attachement?',
        hidecancel: false,
      }
    });
    this.viewOffboardingDocumants();
    dialogRef.afterClosed().subscribe(async result => {
      if (result.data === true) {
        if (this.viewOffboardingAttachements) {
          let id;
          let docTypeId;
          this.viewOffboardingAttachements.forEach(item => {
            if (item?.onboardingDocumentType === controlName) {
              docTypeId = item.docTypeId;
              id = item.id
            }
          })

          const data = {
            onboardingId: this.data.applicant.onboardingid,
            docTypeId: docTypeId,
            id: id
          }
          if (id) {
            const responseData = await this.uploadAttachemntService
              .deletedocument(data)
              .then((response) => {
                return JSON.parse(response);
              })
              .catch((error) => {
                // console.error(error);
                return error;
              });
          }
        }
        this.formGroup.get(controlName)?.reset();
        const fileInput = document.getElementById(controlName) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.formGroup.controls[controlName].markAsTouched();
        this.formGroup.controls[controlName].updateValueAndValidity();
      } else {
        // console.log('User cancelled');
      }
    });
  }

  get departmentDocs(): viewOffboardingAttachements[] {
  return (this.viewOffboardingAttachements ?? [])
    .filter(i => i?.onboardingDocumentType === 'Department Attachment')
    .sort((a, b) => Number(a.id) - Number(b.id));
}

  fileName(controlName: string): string {
    const file = this.formGroup.get(controlName)?.value;
    return file;
  }

  previewAttachment(controlName: string) {
    if (this.viewOffboardingAttachements) {
      let id;
      this.viewOffboardingAttachements.forEach(item => {
        if (item?.onboardingDocumentType === controlName) {
          id = item.id;
        }
      });
      let filename = this.formGroup.get(controlName)?.value;
      this.preview.emit([id, filename]);
    }
  }

  async getOffboardingDetails() {
    this.setSprinner.set(true);
    let repCode = this.data.applicant.repcode;
    const responseData = await this.offboardingMaintananceService
      .getOffboardingDetails(repCode)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {

      this.viewOffboardingDetails = responseData.data.responsedata.off_boarding_details;
      this.viewOffboardingAttachements = responseData.data.responsedata.documents;
      if (this.viewOffboardingDetails != null) {
        this.formGroup.controls['status'].setValue(this.viewOffboardingDetails?.repstatus);
      }
      this.formGroup.controls['resignedDatePias'].setValue(this.viewOffboardingDetails?.dateOfResigned);
      this.formGroup.controls['lastDayOfService'].setValue(this.viewOffboardingDetails?.lastDayOfService);
      this.formGroup.controls['dsuCharges'].setValue(this.viewOffboardingDetails?.dsuCharges);
      this.formGroup.controls['reasonForLeaving'].setValue(this.viewOffboardingDetails?.reasonForLeaving);
      this.formGroup.controls['financeOutstandingPayments'].setValue(this.viewOffboardingDetails.questionFinOutstandingPayment == 'true' ? true : this.viewOffboardingDetails.questionFinOutstandingPayment == 'false' ? false : null);
      this.formGroup.controls['commissionsNegativeBalance'].setValue(this.viewOffboardingDetails.questionComNegativeBalance == 'true' ? true : this.viewOffboardingDetails.questionComNegativeBalance == 'false' ? false : null);
      this.formGroup.controls['commissionsIndemnityExposure'].setValue(this.viewOffboardingDetails.questionComIndemnityExposure == 'true' ? true : this.viewOffboardingDetails.questionComIndemnityExposure == 'false' ? false : null);
      this.formGroup.controls['businessSupportOutstandingPremiums'].setValue(this.viewOffboardingDetails.questionBusGiPolicies == 'true' ? true : this.viewOffboardingDetails.questionBusGiPolicies == 'false' ? false : null);
      this.formGroup.controls['remarks'].setValue(this.viewOffboardingDetails?.dsuRemark);

      if (this.formGroup.get('financeOutstandingPayments')?.value === true) {
        this.formGroup.controls['financeOutstandingPaymentsAmount'].setValue(this.viewOffboardingDetails?.amountFinOutstandingPayment);
        this.formGroup.controls['financeOutstandingPaymentscomments'].setValue(this.viewOffboardingDetails?.remark1);

      }


      if (this.formGroup.get('commissionsNegativeBalance')?.value === true) {
        this.formGroup.controls['commissionsNegativeBalanceAmount'].setValue(this.viewOffboardingDetails?.amountComNegativeBalance);
        this.formGroup.controls['commissionsNegativeBalancecomments'].setValue(this.viewOffboardingDetails?.remark2);
      }

      if (this.formGroup.get('commissionsIndemnityExposure')?.value === true) {
        this.formGroup.controls['commissionsIndemnityExposureAmount'].setValue(this.viewOffboardingDetails?.amountComIndemnityExposure);
        this.formGroup.controls['commissionsIndemnityExposureActions'].setValue(this.viewOffboardingDetails?.actionComIndemnityExposure);
        this.formGroup.controls['commissionsIndemnityExposurecomments'].setValue(this.viewOffboardingDetails?.remark3);
      }

      if (this.formGroup.get('businessSupportOutstandingPremiums')?.value === true) {
        this.formGroup.controls['businessSupportOutstandingPremiumsAmount'].setValue(this.viewOffboardingDetails?.amountBusGiPolicies);
        this.formGroup.controls['businessSupportOutstandingPremiumscomments'].setValue(this.viewOffboardingDetails?.remark4);
      }


      this.viewOffboardingAttachements?.forEach(item => {
        if (item?.onboardingDocumentType === 'managerEmailAttachment') {
          this.formGroup.controls['managerEmailAttachment']?.setValue(item?.fileName);
        }
        if (item?.onboardingDocumentType === 'Department Attachment') {
          this.formGroup.controls['Department Attachment']?.setValue(item?.fileName);
        }
        // if (item?.onboardingDocumentType === 'Commissions Department Attachment') {
        //   this.formGroup.controls['Commissions Department Attachment']?.setValue(item?.fileName);
        // }
        // if (item?.onboardingDocumentType === 'Business Support Department Attachment') {
        //   this.formGroup.controls['Business Support Department Attachment']?.setValue(item?.fileName);
        // }
        if (item?.onboardingDocumentType === 'exitFormAttachment') {
          this.formGroup.controls['exitFormAttachment']?.setValue(item?.fileName);
        }
        if (item?.onboardingDocumentType === 'finalexitFormAttachment') {
          this.formGroup.controls['finalexitFormAttachment']?.setValue(item?.fileName);
        }
      });

      if (this.viewOffboardingDetails?.status == 'offboarding_completed') {
        this.readonly.set(true);
      }
      if (this.readonly() == true) {
        this.formGroup?.disable();
      } else {
        this.formGroup?.enable();
      }

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async saveOffboardingDetails(saveOffboarding: MaintenanceOffboardingModel) {
    this.setSprinner.set(true);
    const responseData = await this.offboardingMaintananceService
      .saveOffboardingDetails(saveOffboarding)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.getOffboardingDetails();
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);

      }
    }
  }
}
